import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ColumnDef,
  PaginationState,
  SortingState,
} from '@tanstack/react-table';
import { API } from '@/utils/API';
import LoadingSpinner from '@/components/ui/loading-spinner';
import { SearchIcon } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { DataTable } from '@/components/ui/DataTable';
import { SubmissionFormData } from '@/features/submission_forms/types';
import PizzaTracker from './PizzaTracker';
import QuickFilters from './QuickFilters';
import { STATUS_OPTIONS } from '@/utils/submissions';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ViewDetailsButton } from '@/components/ui/ViewDetailsButton';
import { formatTableDate } from '@/utils/dates';
import { useSubmissions } from '@/hooks/submissions/useSubmissions';

interface FilterOption {
  value: string;
  label: string;
}

const QUICK_FILTER_COLORS: Record<string, string> = {
  response_required: '#ef4444',
  expiring_soon: '#f59e0b',
};

const DEFAULT_PAGE_SIZE = 25;

export default function SubmissionsTable() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const QUICK_FILTERS = useMemo(
    () => [
      {
        value: 'response_required',
        label: t('submissions.status.response_required'),
        color: QUICK_FILTER_COLORS.response_required,
      },
      {
        value: 'expiring_soon',
        label: t('submissions.status.expiring_soon'),
        color: QUICK_FILTER_COLORS.expiring_soon,
      },
    ],
    [t]
  );
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get('search') || ''
  );
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(
    searchParams.get('search') || ''
  );
  const status = searchParams.get('status') || '';
  const quickFilter = searchParams.get('quick_filter') || '';
  const type = searchParams.get('type') || '';
  const ownerId = searchParams.get('owner_id') || '';

  // Sync search input when URL changes externally (e.g. dashboard navigation)
  const searchQueryRef = React.useRef(searchQuery);
  searchQueryRef.current = searchQuery;
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    if (urlSearch !== searchQueryRef.current) {
      setSearchQuery(urlSearch);
      setDebouncedSearchQuery(urlSearch);
    }
  }, [searchParams]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<{
    id: string;
    title: string;
    status: string;
    display_id: string;
    submission_type?: 'Study' | 'StudySite' | 'Submission';
    owner_id?: string;
  } | null>(null);
  const [types, setTypes] = useState<FilterOption[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Any change that alters which rows/order come back must restart
  // pagination at page 1 — otherwise a user on page 3 could land past the
  // end of a newly narrowed result set.
  useEffect(() => {
    setPagination(prev =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
    );
  }, [debouncedSearchQuery, status, quickFilter, type, ownerId, sorting]);

  const handleViewSubmission = useCallback(
    (submission: {
      id: string;
      title: string;
      status: string;
      display_id: string;
      submission_type?: 'Study' | 'StudySite' | 'Submission';
      owner_id?: string;
    }) => {
      setSelectedSubmission(submission);
      setIsDrawerOpen(true);
    },
    []
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await API.authenticated.fetch('/submissions/filters');
        if (response.ok) {
          const data = await response.json();
          setTypes(data.types || []);
        }
      } catch (_error) {
        // Keep defaults on failure
      }
    };

    fetchFilterOptions();
  }, []);

  // Keep search param in sync with debounced input
  useEffect(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (debouncedSearchQuery) {
          next.set('search', debouncedSearchQuery);
        } else {
          next.delete('search');
        }
        return next;
      },
      { replace: true }
    );
  }, [debouncedSearchQuery, setSearchParams]);

  const sortParam = sorting[0]?.id;
  const orderParam = sorting[0]?.desc ? 'desc' : 'asc';

  const {
    data = [],
    isLoading,
    error,
    pagination: serverPaginationMeta,
  } = useSubmissions({
    search: debouncedSearchQuery,
    status,
    quickFilter,
    type,
    ownerId,
    sort: sortParam || 'created_at',
    order: sortParam ? orderParam : 'desc',
    page: pagination.pageIndex + 1,
    perPage: pagination.pageSize,
  });

  const columns = useMemo(
    () =>
      [
        {
          header: t('submissions.fields.id'),
          accessorKey: 'display_id',
          enableSorting: true,
          meta: { overflow: 'nowrap', maxWidth: 120 },
          cell: ({ getValue }: { getValue: () => string }) => (
            <span className="text-gray-900 font-normal">{getValue()}</span>
          ),
        },
        {
          header: t('submissions.fields.title'),
          accessorKey: 'title',
          enableSorting: true,
          meta: { overflow: 'truncate', minWidth: 240 },
        },
        {
          header: t('submissions.fields.type'),
          accessorKey: 'type',
          enableSorting: false,
          // truncate: long type names (e.g. 'Draft ICF Request') ellipsis instead of
          // pushing Expiration/Status/View columns off screen.
          meta: { overflow: 'truncate', maxWidth: 160 },
          cell: ({ getValue }: { getValue: () => string }) => (
            <span className="text-gray-600">{getValue() || '-'}</span>
          ),
        },
        {
          header: t('submissions.fields.submitted'),
          accessorKey: 'submitted_at',
          enableSorting: true,
          sortingFn: 'nullableDateSort' as const,
          meta: { overflow: 'nowrap', maxWidth: 80 },
          cell: ({ getValue }: { getValue: () => string | null }) => (
            <span className="text-gray-600">{formatTableDate(getValue())}</span>
          ),
        },
        {
          header: t('submissions.fields.expiration'),
          accessorKey: 'expiration_date',
          enableSorting: true,
          sortingFn: 'nullableDateSort' as const,
          meta: { overflow: 'nowrap', maxWidth: 80 },
          cell: ({ getValue }: { getValue: () => string | null }) => (
            <span className="text-gray-600">{formatTableDate(getValue())}</span>
          ),
        },
        {
          header: t('submissions.fields.status'),
          accessorKey: 'status',
          enableSorting: true,
          meta: { overflow: 'nowrap', maxWidth: 120 },
          cell: ({ getValue }: { getValue: () => string }) => (
            <StatusBadge status={getValue()} />
          ),
        },
        {
          id: 'view',
          header: '',
          enableSorting: false,
          meta: { overflow: 'nowrap' },
          cell: ({ row }: { row: { original: SubmissionFormData } }) => (
            /* Hidden accessible link on the first visible column is handled by
               the row click handler; the eye button opens the drawer instead */
            <ViewDetailsButton
              onClick={() => handleViewSubmission(row.original)}
            />
          ),
        },
      ] as ColumnDef<SubmissionFormData, unknown>[],
    [handleViewSubmission, t]
  );

  const handleRowClick = useCallback(
    (row: SubmissionFormData, e: React.MouseEvent | React.KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        window.open(row.show_path, '_blank');
        return;
      }
      navigate(row.show_path);
    },
    [navigate]
  );

  return (
    <>
      <div className="flex items-center gap-4 mb-6 px-8 pt-8">
        <div className="w-80 relative">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={t('submissions.search_placeholder')}
            aria-label={t('submissions.search_placeholder')}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#300091]/20 bg-white text-sm"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <Select
          value={type || 'all'}
          onValueChange={v => {
            setSearchParams(
              prev => {
                const n = new URLSearchParams(prev);
                if (v === 'all') {
                  n.delete('type');
                } else {
                  n.set('type', v);
                }
                return n;
              },
              { replace: true }
            );
          }}
        >
          <SelectTrigger className="px-4 py-2 border border-[#e9eaeb] rounded-md text-sm text-gray-600 font-medium hover:bg-gray-50 bg-white h-auto w-auto gap-2 [&>svg]:opacity-100">
            <span>
              {type
                ? (types.find(o => o.value === type)?.label ??
                  t('submissions.filter.type_label'))
                : t('submissions.filter.type_label')}
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t('submissions.filter.all_types')}
            </SelectItem>
            {types.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={status || 'all'}
          onValueChange={v => {
            setSearchParams(
              prev => {
                const n = new URLSearchParams(prev);
                if (v === 'all') {
                  n.delete('status');
                } else {
                  n.set('status', v);
                }
                return n;
              },
              { replace: true }
            );
          }}
        >
          <SelectTrigger className="px-4 py-2 border border-[#e9eaeb] rounded-md text-sm text-gray-600 font-medium hover:bg-gray-50 bg-white h-auto w-auto gap-2 [&>svg]:opacity-100">
            <span>
              {status
                ? (STATUS_OPTIONS.find(o => o.value === status)?.label ??
                  t('submissions.filter.status_label'))
                : t('submissions.filter.status_label')}
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t('submissions.filter.all_statuses')}
            </SelectItem>
            {STATUS_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <QuickFilters
          filters={QUICK_FILTERS}
          activeFilter={quickFilter}
          onToggle={value => {
            setSearchParams(
              prev => {
                const n = new URLSearchParams(prev);
                if (quickFilter === value) {
                  n.delete('quick_filter');
                } else {
                  n.set('quick_filter', value);
                }
                return n;
              },
              { replace: true }
            );
          }}
        />
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className="text-red-500 text-center p-8">
          {t('submissions.errors.failed_to_load')}
        </div>
      ) : data.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          {t('submissions.empty_yet')}
        </div>
      ) : (
        <div className="mx-8 bg-white border border-[#e9eaeb] rounded-lg overflow-hidden">
          <DataTable
            columns={columns}
            data={data}
            onRowClick={handleRowClick}
            emptyMessage={t('submissions.empty_yet')}
            sorting={sorting}
            onSortingChange={setSorting}
            pagination={{
              pageIndex: pagination.pageIndex,
              pageSize: pagination.pageSize,
              onPaginationChange: setPagination,
              server: {
                pageCount: serverPaginationMeta?.total_pages ?? 0,
                rowCount: serverPaginationMeta?.total ?? 0,
              },
            }}
          />
        </div>
      )}

      {selectedSubmission && (
        <PizzaTracker
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          selectedSubmission={selectedSubmission}
        />
      )}
    </>
  );
}
